import { useEffect, useState } from 'react';
import { logEvent } from '@cortex/faro';
import { fetchCalendarImportInfo, importCalendar } from '@/api/client';
import { toMessage } from '@/api/to-message';
import { importRange, IMPORT_RANGE_FUTURE_MONTHS } from '@/tasks/calendar-range';
import type { TeamId } from '@/tasks/task';

/**
 * 取り込みの進行状態。
 */
type ImportState =
  | { step: 'idle' }
  | { step: 'confirming' }
  | { step: 'running' }
  | { step: 'done'; imported: number; updated: number; removed: number; skipped: number }
  | { step: 'failed'; message: string };

/**
 * CalendarImportButton の props。
 */
export interface CalendarImportButtonProps {
  /** 取り込み先のチーム */
  team: TeamId;
  /** 取り込み対象期間の基準日（`YYYY-MM-DD`）。当日を渡す */
  baseDate: string;
  /** 担当の選択肢 */
  assignees: readonly string[];
  /** 担当の初期値。空なら選択肢の先頭 */
  defaultAssignee?: string;
  /** 取り込みが終わったときに呼ばれる。タスクを読み直すため */
  onImported?: () => void;
}

/** @graph-connects none */
const QUIET_BUTTON =
  'rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-2 py-0.5 text-xs text-[var(--color-text-secondary)]';

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business カレンダー取り込みのボタン。押すと期間と担当を確認し、ログインしている本人の Google カレンダーの予定を会議のタスクとして取り込む。取り込み済みの予定は二重に作らない
 * @graph-connects importRange [calls] 取り込み対象の期間を求める
 * @graph-connects importCalendar [calls] 取り込みの API を呼ぶ
 * @graph-connects fetchCalendarImportInfo [calls] 共有先と、ログインしている人に結びついた担当を引く
 */
export function CalendarImportButton({
  team,
  baseDate,
  assignees,
  defaultAssignee = '',
  onImported,
}: CalendarImportButtonProps) {
  const [state, setState] = useState<ImportState>({ step: 'idle' });
  const [assignee, setAssignee] = useState('');
  // カレンダーを共有する先と、ログインしている人に結びついた担当。確認を開いたときに引く。
  // 担当が結びついていれば選ばせない（メンバー設定のメールアドレスで決まる）
  const [info, setInfo] = useState<{ shareWith: string; assignee: string | null } | null>(null);
  const [infoFailed, setInfoFailed] = useState(false);
  useEffect(() => {
    if (state.step !== 'confirming') {
      return;
    }
    let cancelled = false;
    // 引けなくても取り込みは試せる（担当を選ばせ、共有されていなければ API が手順つきで断る）
    fetchCalendarImportInfo(team)
      .then((next) => {
        if (!cancelled) {
          setInfo(next);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setInfoFailed(true);
        }
        logEvent('calendar_share_address_failed', {
          reason: error instanceof Error ? error.message : String(error),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [state.step, team]);
  const shareWith = info?.shareWith ?? '';
  const linked = info?.assignee ?? null;
  // 担当がまだ分からない間は取り込ませない（引けなかったときは選ばせる）
  const infoLoading = info === null && !infoFailed;

  // 期間は選ばせず固定する（当日 〜 1 ヶ月後）
  const { from, to } = importRange(baseDate);
  // 選んでいなければ、絞り込み中の担当か選択肢の先頭を使う
  const chosen =
    linked ??
    (assignee !== '' ? assignee : defaultAssignee !== '' ? defaultAssignee : (assignees[0] ?? ''));

  const run = async () => {
    setState({ step: 'running' });
    try {
      const result = await importCalendar(team, from, to, chosen);
      setState({ step: 'done', ...result });
      onImported?.();
    } catch (cause: unknown) {
      setState({ step: 'failed', message: toMessage(cause) });
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-3">
      <button
        type="button"
        disabled={state.step === 'running'}
        onClick={() => {
          setState({ step: 'confirming' });
        }}
        className={
          state.step === 'running'
            ? 'shrink-0 rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-subtle)] px-3 py-1.5 text-sm text-[var(--color-text-muted)]'
            : 'shrink-0 rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-3 py-1.5 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
        }
      >
        カレンダー取り込み
      </button>

      {state.step === 'confirming' ? (
        <div className="flex flex-wrap items-center gap-2 rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-subtle)] px-3 py-1.5 text-sm">
          <span className="text-[var(--color-text-primary)]">
            <span className="tabular-nums">
              {from} 〜 {to}
            </span>
            の予定を、会議として取り込みますか？
          </span>
          {/* 担当は、メンバー設定のメールアドレスでログインしている人に結びついていればその人に決まる。
              結びついていなければ選ばせる */}
          {infoLoading ? (
            <span className="text-xs text-[var(--color-text-muted)]">担当を確認しています…</span>
          ) : linked !== null ? (
            <span className="text-xs text-[var(--color-text-secondary)]">
              担当 <span className="font-medium text-[var(--color-text-primary)]">{linked}</span>
              （ログイン中のアカウント）
            </span>
          ) : (
            <label className="flex items-center gap-1 text-xs text-[var(--color-text-secondary)]">
              担当
              <select
                aria-label="取り込むタスクの担当"
                value={chosen}
                onChange={(event) => {
                  setAssignee(event.target.value);
                }}
                className="rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-1.5 py-0.5 text-xs"
              >
                {assignees.length === 0 ? <option value="">（担当なし）</option> : null}
                {assignees.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <button
            type="button"
            onClick={() => {
              setState({ step: 'idle' });
            }}
            className={QUIET_BUTTON}
          >
            取りやめる
          </button>
          <button
            type="button"
            disabled={chosen === '' || infoLoading}
            onClick={() => {
              void run();
            }}
            className="rounded bg-[var(--color-accent)] px-2 py-0.5 text-xs font-medium text-[var(--color-bg-surface)] disabled:opacity-50"
          >
            取り込む
          </button>
          {!infoLoading && linked === null ? (
            <p className="m-0 w-full text-xs leading-relaxed text-[var(--color-text-secondary)]">
              設定 &gt; メンバー設定でご自分のメールアドレスを登録すると、担当が自動で決まります
            </p>
          ) : null}
          {shareWith === '' ? null : (
            <p className="m-0 w-full text-xs leading-relaxed text-[var(--color-text-secondary)]">
              はじめての場合は、Google カレンダーの設定で
              <span className="mx-1 font-medium break-all text-[var(--color-text-primary)] select-all">
                {shareWith}
              </span>
              を「予定の表示（すべての予定の詳細）」で共有してください（共有したカレンダーだけを読みます）
            </p>
          )}
        </div>
      ) : null}

      {state.step === 'running' ? (
        <span role="status" className="text-xs text-[var(--color-text-secondary)]">
          カレンダーから取り込んでいます…
        </span>
      ) : null}

      {state.step === 'done' || state.step === 'failed' ? (
        <div
          role="status"
          className="flex flex-wrap items-center gap-2 rounded border border-[var(--color-doing-border)] bg-[var(--color-doing-bg)] px-3 py-1.5 text-sm text-[var(--color-doing-text)]"
        >
          <span>
            {state.step === 'done'
              ? importSummary(state)
              : `取り込めませんでした：${state.message}`}
          </span>
          <button
            type="button"
            aria-label="閉じる"
            onClick={() => {
              setState({ step: 'idle' });
            }}
            className="shrink-0 px-1 text-base leading-none"
          >
            ×
          </button>
        </div>
      ) : null}

      {state.step === 'idle' ? (
        <span className="text-xs text-[var(--color-text-muted)]">
          当日から {IMPORT_RANGE_FUTURE_MONTHS} ヶ月後まで
        </span>
      ) : null}
    </div>
  );
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 取り込みの結果を 1 文にする。追加・更新・削除のうち 0 件のものは書かない。何も変わらなければそう伝える
 * @graph-connects none
 */
export function importSummary(result: {
  imported: number;
  updated: number;
  removed: number;
  skipped: number;
}): string {
  const parts = [
    result.imported > 0 ? `${String(result.imported)} 件を追加` : '',
    result.updated > 0 ? `${String(result.updated)} 件を更新` : '',
    result.removed > 0 ? `${String(result.removed)} 件を削除` : '',
  ].filter((part) => part !== '');
  return parts.length === 0 ? '変わった予定はありませんでした' : `${parts.join('・')}しました`;
}
